import styled from "styled-components";
import { useEffect, useState } from "react";
import { Collapse, Form, InputGroup } from "react-bootstrap";
import { PRICE_TYPE } from "../strings";
import { CustomDiv } from "./lead-agent-styles";
import {
  getLeadServicePlusFixedPriceVariants,
  getLeadServicePlusPriceVariants
} from "../lead-service-plus-price-variants";
import { useTranslation } from "react-i18next";

const AgentQuestionDiv = styled.div`
  font-size: 16px;
`;

const AgentPricePrompt = ({
  leadFunnel,
  setCommentPrice,
  onCompletionChange = () => {}
}) => {
  const { t } = useTranslation();
  const { FIXED, DYNAMIC, AREA_QUERY } = PRICE_TYPE;
  const initialPriceRange = {
    lowerBound: 0,
    upperBound: 0
  };
  const budgetAreaQueryBounds = leadFunnel?.budgetAreaOfQueryBounds ?? 20; // default is 20%
  const nameOfAccountingUnit =
    leadFunnel?.nameOfAccountingUnitForLeadAgent || leadFunnel?.category?.unit;
  const priceVariants = getLeadServicePlusPriceVariants(leadFunnel);
  const fixedPriceVariants = getLeadServicePlusFixedPriceVariants(leadFunnel);
  const isDynamicPriceType = leadFunnel?.leadServicePlusPriceType === DYNAMIC;
  const isFixedPriceType = leadFunnel?.leadServicePlusPriceType === FIXED;
  const activePriceVariants = isFixedPriceType
    ? fixedPriceVariants
    : priceVariants;
  const hasMultiplePriceVariants =
    (isDynamicPriceType || isFixedPriceType) && activePriceVariants.length > 1;
  const forFixedPrice =
    isFixedPriceType &&
    (leadFunnel?.fixedPricePerInvoiceUnitForLeadAgent ||
      fixedPriceVariants[0]?.price);
  const forDynamicPriceCalculation =
    isDynamicPriceType &&
    (leadFunnel?.pricePerInvoiceUnitForLeadAgent || priceVariants[0]?.price);
  const forAreaQueryPriceCalculation =
    leadFunnel?.leadServicePlusPriceType === AREA_QUERY &&
    leadFunnel?.minBudgetQuery &&
    leadFunnel?.maxBudgetQuery &&
    leadFunnel?.pricePerAreaBelowMinBudget &&
    leadFunnel?.pricePerAreaBetweenBudget &&
    leadFunnel?.pricePerAreaAboveMaxBudget;
  const [priceRange, setPriceRange] = useState(initialPriceRange);
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [agentInputValue, setAgentInputValue] = useState("");
  const updateCompletion = ({ lowerBound = 0, upperBound = 0 } = {}) => {
    onCompletionChange({
      isRequired: true,
      isAnswered: lowerBound > 0 && upperBound > 0
    });
  };
  const selectedPriceVariant = hasMultiplePriceVariants
    ? activePriceVariants.find((variant) => variant.id === selectedVariantId)
    : activePriceVariants[0];
  const selectedVariantPrice =
    selectedPriceVariant?.price ||
    (isFixedPriceType
      ? leadFunnel?.fixedPricePerInvoiceUnitForLeadAgent
      : leadFunnel?.pricePerInvoiceUnitForLeadAgent);

  const getAreaValue = (agentInput) => {
    let areaValue = 0;
    if (agentInput <= leadFunnel.minBudgetQuery) {
      areaValue = leadFunnel.pricePerAreaBelowMinBudget;
    } else if (
      agentInput > leadFunnel.minBudgetQuery &&
      agentInput <= leadFunnel.maxBudgetQuery
    ) {
      areaValue = leadFunnel.pricePerAreaBetweenBudget;
    } else if (agentInput > leadFunnel.maxBudgetQuery) {
      areaValue = leadFunnel.pricePerAreaAboveMaxBudget;
    }
    return areaValue;
  };

  const calculatePriceRange = ({
    value = 0,
    priceType,
    pricePerInvoiceUnit
  }) => {
    const multipliedValue =
      priceType === FIXED
        ? pricePerInvoiceUnit
        : priceType === DYNAMIC
          ? pricePerInvoiceUnit * value
          : getAreaValue(value);
    const _range = (multipliedValue / 100) * budgetAreaQueryBounds;
    const lowerBound = multipliedValue - _range;
    const upperBound = multipliedValue + _range;
    setPriceRange({ lowerBound, upperBound });
    setCommentPrice({ lowerBound, upperBound });
    updateCompletion({ lowerBound, upperBound });
  };

  const handleOnChange = (value) => {
    setAgentInputValue(value);
    if (
      value?.length &&
      (forDynamicPriceCalculation || forAreaQueryPriceCalculation)
    ) {
      if (hasMultiplePriceVariants && !selectedPriceVariant) {
        setPriceRange(initialPriceRange);
        setCommentPrice(initialPriceRange);
        updateCompletion(initialPriceRange);
        return;
      }
      calculatePriceRange({
        value,
        pricePerInvoiceUnit: selectedVariantPrice,
        priceType: forDynamicPriceCalculation
          ? DYNAMIC
          : forAreaQueryPriceCalculation
            ? AREA_QUERY
            : ""
      });
    } else {
      setPriceRange(initialPriceRange);
      setCommentPrice(initialPriceRange);
      updateCompletion(initialPriceRange);
    }
  };

  const handleVariantChange = (value) => {
    setSelectedVariantId(value);
    setPriceRange(initialPriceRange);
    setCommentPrice(initialPriceRange);
    const variant = activePriceVariants.find((item) => item.id === value);
    if (forFixedPrice && variant) {
      calculatePriceRange({
        priceType: FIXED,
        pricePerInvoiceUnit:
          variant.price || leadFunnel?.fixedPricePerInvoiceUnitForLeadAgent
      });
    }
  };

  useEffect(() => {
    if (forFixedPrice && (!hasMultiplePriceVariants || selectedPriceVariant)) {
      calculatePriceRange({
        priceType: FIXED,
        pricePerInvoiceUnit: selectedVariantPrice
      });
    } else if (forFixedPrice) {
      setPriceRange(initialPriceRange);
      setCommentPrice(initialPriceRange);
      updateCompletion(initialPriceRange);
    } else if (forDynamicPriceCalculation || forAreaQueryPriceCalculation) {
      updateCompletion(initialPriceRange);
    } else {
      onCompletionChange({
        isRequired: false,
        isAnswered: true
      });
    }
    // eslint-disable-next-line
  }, [leadFunnel, selectedVariantId]);

  useEffect(() => {
    if (agentInputValue && !isFixedPriceType) {
      handleOnChange(agentInputValue);
    }
    // eslint-disable-next-line
  }, [selectedVariantId]);

  if (
    !forFixedPrice &&
    !forDynamicPriceCalculation &&
    !forAreaQueryPriceCalculation
  ) {
    return <></>;
  }

  return (
    <CustomDiv className="mt-4">
      {hasMultiplePriceVariants && (
        <div className="mb-2">
          <Form.Label>
            {t("funnelIntegration.leadServicePlus.variantSelectLabel")}
          </Form.Label>
          <Form.Select
            className="w-50"
            value={selectedVariantId}
            onChange={({ target: { value } }) => handleVariantChange(value)}
          >
            <option value="">
              {t("funnelIntegration.leadServicePlus.variantSelectPlaceholder")}
            </option>
            {activePriceVariants.map((variant) => (
              <option key={variant.id} value={variant.id}>
                {variant.name}
              </option>
            ))}
          </Form.Select>
        </div>
      )}
      {forDynamicPriceCalculation || forAreaQueryPriceCalculation ? (
        <div
          className="mb-2"
          hidden={hasMultiplePriceVariants && !selectedVariantId}
        >
          <Form.Label>{nameOfAccountingUnit}</Form.Label>
          <InputGroup className="w-50">
            <Form.Control
              type="number"
              placeholder={t(
                "funnelIntegration.leadServicePlus.specifyHerePlaceholder"
              )}
              onChange={({ target: { value } }) => handleOnChange(value)}
              onWheel={({ target }) => target.blur()}
            />
          </InputGroup>
        </div>
      ) : (
        <></>
      )}
      <Collapse in={priceRange.lowerBound > 0 && priceRange.upperBound > 0}>
        <div>
          <AgentQuestionDiv className="p-3 border bg-white rounded">
            {/* check deviation = 0% */}
            {priceRange.lowerBound === priceRange.upperBound
              ? `Ok, das, was Sie planen, können wir abbilden. Unsere Produkte in diesem Bereich starten bei ${priceRange.lowerBound}. Haben Sie soviel eingeplant?`
              : `Ok, das, was Sie planen, können wir abbilden. Das würde vom
          Kostenpunkt zwischen ${priceRange.lowerBound}€ und ${priceRange.upperBound}€ liegen. Haben Sie so
          viel eingeplant?`}
          </AgentQuestionDiv>
        </div>
      </Collapse>
    </CustomDiv>
  );
};
export default AgentPricePrompt;
